/**
 * Shop/Regelwerk: Suche, Filter und Trefferliste der Advantis-Artikel.
 *
 * Ersetzt ShopOverview.cshtml und den Filter-Teil von multidigital.js der alten
 * Seite. Gesucht wird weiterhin über Examine - serverseitig in ShopSearchService,
 * abgefragt über /umbraco/md/shop/search. Die URL behält die alten Parameter
 * (search, cat0..catN, page), damit Links und der Zurück-Knopf funktionieren.
 *
 * Wie alt wirkt eine geänderte Auswahl erst mit "Übernehmen" bzw. "Suchen".
 */

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { label, type ShopSearchResult, type ShopSelection } from './shop-types.ts';
import './shop-filter-group.ts';
import './shop-pager.ts';
import './shop-tile.ts';

interface ShopQuery {
    search: string;
    selection: ShopSelection;
    page: number;
}

@customElement('svgw-shop')
export class SvgwShop extends LitElement {
    /** Endpunkt der Suche. */
    @property({ type: String }) api = '/umbraco/md/shop/search';
    /** Key der Shop-Seite - liefert Filtergruppen und Artikel pro Seite. */
    @property({ type: String }) node = '';
    @property({ type: String }) culture = 'de';
    /** Suchbegriff der Erstanzeige. */
    @property({ type: String }) search = '';

    @state() private data: ShopSearchResult | null = null;
    @state() private query: ShopQuery = { search: '', selection: {}, page: 1 };
    /** Auswahl, die noch nicht übernommen ist. */
    @state() private pending: ShopSelection = {};
    @state() private searchInput = '';
    @state() private openGroup: string | null = null;
    @state() private loading = false;
    @state() private error = false;

    private abort: AbortController | null = null;

    private readonly onPopState = (): void => {
        this.query = this.readUrl();
        this.searchInput = this.query.search;
        void this.load();
    };

    private readonly onDocumentClick = (event: MouseEvent): void => {
        if (this.openGroup !== null && !(event.target instanceof Node && this.querySelector('.shop__filters')?.contains(event.target))) {
            this.openGroup = null;
        }
    };

    protected createRenderRoot(): HTMLElement {
        return this;
    }

    connectedCallback(): void {
        super.connectedCallback();

        this.query = this.readUrl();
        this.searchInput = this.query.search || this.search;

        // Erstanzeige vom Server übernehmen; das Script-Element danach entfernen.
        const initial = this.querySelector<HTMLScriptElement>('script[data-shop-initial]');
        if (initial !== null) {
            try {
                this.applyResult(JSON.parse(initial.textContent ?? 'null') as ShopSearchResult);
            } catch (err) {
                console.error('Shop: Erstanzeige nicht lesbar', err);
            }
            initial.remove();
        }

        if (this.data === null) {
            void this.load();
        }

        window.addEventListener('popstate', this.onPopState);
        document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback(): void {
        window.removeEventListener('popstate', this.onPopState);
        document.removeEventListener('click', this.onDocumentClick);
        this.abort?.abort();
        super.disconnectedCallback();
    }

    // --------------------------------------------------------------- URL

    private readUrl(): ShopQuery {
        const params = new URLSearchParams(window.location.search);
        const selection: ShopSelection = {};

        for (const key of new Set(params.keys())) {
            if (/^cat\d+$/.test(key)) {
                selection[key] = params.getAll(key).flatMap((v) => v.split(',')).map((v) => v.trim()).filter((v) => v.length > 0);
            }
        }

        const page = Number.parseInt(params.get('page') ?? '1', 10);
        return { search: params.get('search') ?? '', selection, page: Number.isFinite(page) && page > 0 ? page : 1 };
    }

    private toParams(query: ShopQuery): URLSearchParams {
        const params = new URLSearchParams();
        if (query.search.trim().length > 0) {
            params.set('search', query.search.trim());
        }

        for (const [key, ids] of Object.entries(query.selection)) {
            if (ids.length > 0) {
                params.set(key, ids.join(','));
            }
        }

        if (query.page > 1) {
            params.set('page', String(query.page));
        }

        return params;
    }

    private pageHref = (page: number): string => {
        const params = this.toParams({ ...this.query, page });
        const qs = params.toString();
        return qs.length > 0 ? `${window.location.pathname}?${qs}` : window.location.pathname;
    };

    /** Neue Abfrage: in die Adresszeile schreiben und laden. */
    private navigate(query: ShopQuery): void {
        this.query = query;
        this.openGroup = null;
        window.history.pushState(null, '', this.pageHref(query.page));
        void this.load();
    }

    // -------------------------------------------------------------- Laden

    private async load(): Promise<void> {
        this.abort?.abort();
        const abort = new AbortController();
        this.abort = abort;

        const params = this.toParams(this.query);
        params.set('culture', this.culture);
        if (this.node.length > 0) {
            params.set('node', this.node);
        }

        this.loading = true;
        this.error = false;

        try {
            const response = await fetch(`${this.api}?${params.toString()}`, { signal: abort.signal, headers: { Accept: 'application/json' } });
            if (!response.ok) {
                throw new Error(`Request failed: ${response.status}`);
            }

            this.applyResult((await response.json()) as ShopSearchResult);
        } catch (err) {
            if (abort.signal.aborted) {
                return;
            }

            console.error('Shop: Suche fehlgeschlagen', err);
            this.error = true;
        } finally {
            if (this.abort === abort) {
                this.loading = false;
            }
        }
    }

    /** Die Auswahl kommt bereinigt vom Server zurück - sie gilt als übernommen. */
    private applyResult(result: ShopSearchResult): void {
        const selection: ShopSelection = {};
        for (const group of result.groups) {
            const ids = group.items.filter((i) => i.checked).map((i) => i.id);
            if (ids.length > 0) {
                selection[group.key] = ids;
            }
        }

        this.data = result;
        this.query = { ...this.query, selection, page: result.page };
        this.pending = selection;
    }

    // ------------------------------------------------------------ Aktionen

    private submitSearch(event: Event): void {
        event.preventDefault();
        this.navigate({ search: this.searchInput, selection: this.pending, page: 1 });
    }

    private applyFilters(): void {
        this.navigate({ search: this.searchInput, selection: this.pending, page: 1 });
    }

    private resetFilters(): void {
        this.pending = {};
        this.navigate({ search: this.searchInput, selection: {}, page: 1 });
    }

    private changePage(page: number): void {
        this.navigate({ ...this.query, page });
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /** Die Pflichtgruppe (Sprache) zählt nicht - sie ist immer gesetzt. */
    private hasFilters(): boolean {
        const required = new Set(this.data?.groups.filter((g) => g.required).map((g) => g.key) ?? []);
        return Object.entries(this.pending).some(([key, ids]) => !required.has(key) && ids.length > 0);
    }

    // ------------------------------------------------------------- Anzeige

    protected render(): TemplateResult {
        const data = this.data;

        return html`
            <form class="shop__searchmask" role="search" @submit=${this.submitSearch}>
                <input type="search" class="form-control" name="search" .value=${this.searchInput}
                       placeholder=${label('Shop.SearchPlaceholder')} aria-label=${label('Shop.SearchPlaceholder')}
                       @input=${(e: Event) => { this.searchInput = (e.target as HTMLInputElement).value; }}>
                <button type="submit" class="btn btn-primary">${label('Shop.Search')}</button>
            </form>

            ${data !== null && data.groups.length > 0 ? html`
                <div class="shop__filters">
                    ${data.groups.map((group) => html`
                        <svgw-shop-filter-group
                            .group=${group}
                            .selected=${this.pending[group.key] ?? []}
                            .open=${this.openGroup === group.key}
                            @shop-toggle=${() => { this.openGroup = this.openGroup === group.key ? null : group.key; }}
                            @shop-change=${(e: CustomEvent<string[]>) => { this.pending = { ...this.pending, [group.key]: e.detail }; }}
                            @shop-apply=${this.applyFilters}>
                        </svgw-shop-filter-group>`)}
                    ${this.hasFilters() ? html`
                        <button type="button" class="btn btn-link btn-sm shop__reset" @click=${this.resetFilters}>
                            ${label('Shop.ResetFilter')}
                        </button>` : nothing}
                </div>` : nothing}

            <div class="shop__results ${this.loading ? 'is-loading' : ''}" aria-live="polite" aria-busy=${this.loading}>
                ${this.renderResults(data)}
            </div>
        `;
    }

    private renderResults(data: ShopSearchResult | null): TemplateResult | typeof nothing {
        if (this.error) {
            return html`<p class="shop__message">${label('Shop.Error')}</p>`;
        }

        if (data === null) {
            return nothing;
        }

        if (data.items.length === 0) {
            return html`<p class="shop__message">${label('Shop.NoResults')}</p>`;
        }

        return html`
            <p class="shop__count small">${label('Shop.Results', data.totalCount)}</p>
            <div class="row g-4 shop__grid">
                ${data.items.map((item) => html`
                    <div class="col-6 col-md-4 col-lg-3">
                        <svgw-shop-tile .item=${item}></svgw-shop-tile>
                    </div>`)}
            </div>
            <svgw-shop-pager class="d-block mt-4" .page=${data.page} .totalPages=${data.totalPages} .href=${this.pageHref}
                             @shop-page=${(e: CustomEvent<number>) => this.changePage(e.detail)}></svgw-shop-pager>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'svgw-shop': SvgwShop;
    }
}
